import React, { useState, useEffect } from "react";
import { Box, Typography, Grid, ToggleButton, ToggleButtonGroup, Tooltip as MuiTooltip } from "@mui/material";
import {
  Radar, RadarChart, PolarGrid, PolarAngleAxis, ResponsiveContainer,
  Tooltip, BarChart, XAxis, YAxis, Bar, PieChart, Pie, Cell, Legend,
  CartesianGrid, LineChart, Line,
} from "recharts";
import { getStatsUserAction, cleanStatsAction } from "../redux/actions/stats";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import strings from "../i18n/language";
import PeopleRoundedIcon from "@mui/icons-material/PeopleRounded";
import PersonAddRoundedIcon from "@mui/icons-material/PersonAddRounded";
import GroupRoundedIcon from "@mui/icons-material/GroupRounded";
import StarsRoundedIcon from "@mui/icons-material/StarsRounded";
import NotificationsActiveRoundedIcon from "@mui/icons-material/NotificationsActiveRounded";
import CardGiftcardRoundedIcon from "@mui/icons-material/CardGiftcardRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";

const BRAND = '#3b95e8';
const GLASS = 'rgba(255,255,255,0.04)';
const BORDER = 'rgba(255,255,255,0.07)';
const MUTED = 'rgba(232,240,254,0.38)';

const CHART_COLORS = ['#3b95e8', '#5bbfb5', '#8b9fd4', '#6baed6', '#94c8f7', '#74c0a8'];

const ageRanges = [
  { name: "unknown", min: null, max: null, value: 0 },
  { name: "<18",     min: 0,    max: 18,   value: 0 },
  { name: "18-24",   min: 18,   max: 24,   value: 0 },
  { name: "25-34",   min: 25,   max: 34,   value: 0 },
  { name: "35-44",   min: 35,   max: 44,   value: 0 },
  { name: "45-54",   min: 45,   max: 54,   value: 0 },
  { name: "55-64",   min: 55,   max: 64,   value: 0 },
  { name: ">64",     min: 65,   max: Infinity, value: 0 },
];

function getWeekMonday(weekNumber) {
  const year = new Date().getFullYear();
  const jan4 = new Date(year, 0, 4);
  const monday = new Date(jan4);
  monday.setDate(jan4.getDate() - ((jan4.getDay() + 6) % 7) + (weekNumber - 1) * 7);
  return monday;
}

function getWeekLabel(weekNumber) {
  const monday = getWeekMonday(weekNumber);
  const dd = String(monday.getDate()).padStart(2, "0");
  const mm = String(monday.getMonth() + 1).padStart(2, "0");
  return `S${weekNumber} ${dd}/${mm}`;
}

// Buckets a week (identified by its Monday) into the month that Monday falls in.
// Approximate: a week straddling two months is attributed entirely to its Monday's month.
function getMonthKey(weekNumber) {
  const monday = getWeekMonday(weekNumber);
  return `${monday.getFullYear()}-${String(monday.getMonth() + 1).padStart(2, "0")}`;
}

function getMonthLabel(monthKey) {
  const [year, month] = monthKey.split("-");
  return `${month}/${year}`;
}

/* ── Shared primitives ── */

const card = {
  background: GLASS,
  border: `1px solid ${BORDER}`,
  borderRadius: 20,
  backdropFilter: 'blur(12px)',
  padding: '24px 28px',
  height: '100%',
  boxSizing: 'border-box',
};

const chartAxis = { fill: MUTED, fontSize: 11 };
const chartGrid = { stroke: 'rgba(255,255,255,0.06)', strokeDasharray: '4 4' };

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: 'rgba(13,27,54,0.96)',
      border: `1px solid ${BORDER}`,
      borderRadius: 10,
      padding: '10px 14px',
      fontSize: 12,
      color: '#e8f0fe',
      backdropFilter: 'blur(8px)',
    }}>
      {label && <div style={{ marginBottom: 6, color: MUTED, fontWeight: 600 }}>{label}</div>}
      {payload.map((p) => (
        <div key={p.dataKey || p.name} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
          <div style={{ width: 8, height: 8, borderRadius: 2, background: p.fill || p.color }} />
          <span style={{ color: 'rgba(232,240,254,0.6)' }}>{p.name}:</span>
          <span style={{ fontWeight: 700 }}>{p.value}</span>
        </div>
      ))}
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, info, accent }) => (
  <div style={{ ...card, display: 'flex', flexDirection: 'column', gap: 8, position: 'relative', overflow: 'hidden' }}>
    <div style={{
      position: 'absolute', top: 0, left: 0, right: 0, height: 2,
      background: `linear-gradient(90deg, ${accent || BRAND}, transparent)`,
      borderRadius: '20px 20px 0 0',
    }} />
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <Typography style={{ fontSize: 12, fontWeight: 600, color: MUTED, letterSpacing: 0.5, textTransform: 'uppercase' }}>
        {label}
      </Typography>
      {Icon && (
        <div style={{
          width: 32, height: 32, borderRadius: 8,
          background: `rgba(59,149,232,0.10)`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Icon style={{ fontSize: 16, color: accent || BRAND }} />
        </div>
      )}
    </div>
    <Typography style={{ fontSize: 42, fontWeight: 700, color: '#e8f0fe', lineHeight: 1 }}>
      {value ?? '—'}
    </Typography>
    {info && (
      <Typography style={{ fontSize: 12, color: MUTED, marginTop: 4 }}>{info}</Typography>
    )}
  </div>
);

const ChartCard = ({ title, height = 300, action, children }) => (
  <div style={card}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
      <Typography style={{ fontSize: 14, fontWeight: 600, color: '#e8f0fe', letterSpacing: 0.3 }}>
        {title}
      </Typography>
      {action}
    </div>
    <ResponsiveContainer width="100%" height={height}>
      {children}
    </ResponsiveContainer>
  </div>
);

const TopClientsCard = ({ clients, viewMode, onViewModeChange, monthLabel, onPrevMonth, onNextMonth, isCurrentMonth }) => {
  const rankAccent = ['#f5c451', '#c9ccd6', '#c98a4b'];

  const getLabel = (client) => {
    if (client.displayName && client.displayName !== 'Anonymous') return client.displayName;
    return `${strings.LABEL_DASHBOARD_TOP_CLIENTS_ANONYMOUS} ${client.userId.slice(0, 6)}`;
  };

  // "5 regalos: 2 canjeados, 2 expirados, 1 pendiente": how the client's gifts
  // ended, only the parts that happened. Each label is "singular|plural"
  const plural = (n, label) => `${n} ${label.split('|')[n === 1 ? 0 : 1]}`;
  const giftsSummary = (client) => {
    const parts = [
      [Number(client.giftsRedeemed) || 0, strings.LABEL_DASHBOARD_TOP_CLIENTS_REDEEMED],
      [Number(client.giftsExpired) || 0, strings.LABEL_DASHBOARD_TOP_CLIENTS_EXPIRED],
      [Number(client.giftsPending) || 0, strings.LABEL_DASHBOARD_TOP_CLIENTS_PENDING],
    ].filter(([n]) => n > 0);
    const total = parts.reduce((sum, [n]) => sum + n, 0);
    if (!total) return strings.LABEL_DASHBOARD_TOP_CLIENTS_NO_GIFTS;
    return `${plural(total, strings.LABEL_DASHBOARD_TOP_CLIENTS_GIFTS)}: ${parts.map(([n, label]) => plural(n, label)).join(', ')}`;
  };

  return (
    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: viewMode === 'month' ? 8 : 20 }}>
        <Typography style={{ fontSize: 14, fontWeight: 600, color: '#e8f0fe', letterSpacing: 0.3 }}>
          {strings.LABEL_DASHBOARD_TOP_CLIENTS}
        </Typography>
        <ChartToggle
          value={viewMode}
          onChange={onViewModeChange}
          options={[
            { value: 'all', label: strings.LABEL_VIEW_ALL_TIME },
            { value: 'month', label: strings.LABEL_VIEW_MONTHLY },
          ]}
        />
      </div>
      {viewMode === 'month' && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginBottom: 12 }}>
          <button onClick={onPrevMonth} style={{ background: 'none', border: 'none', cursor: 'pointer', color: MUTED, display: 'flex', padding: 4 }}>
            <ChevronLeftRoundedIcon style={{ fontSize: 18 }} />
          </button>
          <Typography style={{ fontSize: 12, fontWeight: 600, color: MUTED, minWidth: 120, textAlign: 'center', textTransform: 'capitalize' }}>
            {monthLabel}
          </Typography>
          <button
            onClick={onNextMonth}
            disabled={isCurrentMonth}
            style={{ background: 'none', border: 'none', cursor: isCurrentMonth ? 'default' : 'pointer', color: isCurrentMonth ? 'rgba(232,240,254,0.15)' : MUTED, display: 'flex', padding: 4 }}
          >
            <ChevronRightRoundedIcon style={{ fontSize: 18 }} />
          </button>
        </div>
      )}
      {clients && clients.length ? (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {clients.map((client, index) => (
            <div key={client.userId} style={{
              display: 'flex', alignItems: 'center', gap: 12,
              padding: '10px 2px',
              borderBottom: index < clients.length - 1 ? `1px solid ${BORDER}` : 'none',
            }}>
              <div style={{
                width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 11, fontWeight: 700,
                color: index < 3 ? '#0a1628' : MUTED,
                background: rankAccent[index] || 'rgba(255,255,255,0.06)',
              }}>
                {index + 1}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <Typography style={{
                  fontSize: 13, fontWeight: 600, color: '#e8f0fe',
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}>
                  {getLabel(client)}
                </Typography>
                <Typography style={{ fontSize: 11, color: MUTED, marginTop: 1 }}>
                  {giftsSummary(client)}
                </Typography>
              </div>
              <Typography style={{ fontSize: 16, fontWeight: 700, color: BRAND, flexShrink: 0 }}>
                {client.totalPoints}
              </Typography>
            </div>
          ))}
        </div>
      ) : <EmptyChart />}
    </div>
  );
};

const DashboardTabs = ({ tabs, active, onChange }) => (
  <div style={{ display: 'flex', gap: 4, marginBottom: 24, borderBottom: `1px solid ${BORDER}`, overflowX: 'auto' }}>
    {tabs.map((tab, i) => (
      <button
        key={tab.key}
        onClick={() => onChange(i)}
        style={{
          background: 'none',
          border: 'none',
          borderBottom: active === i ? `2px solid ${BRAND}` : '2px solid transparent',
          color: active === i ? '#e8f0fe' : MUTED,
          fontSize: 13,
          fontWeight: active === i ? 700 : 500,
          padding: '10px 16px',
          cursor: 'pointer',
          whiteSpace: 'nowrap',
          transition: 'color 0.15s, border-color 0.15s',
        }}
      >
        {tab.label}
      </button>
    ))}
  </div>
);

const ChartToggle = ({ value, onChange, options }) => {
  const opts = options || [
    { value: 'week', label: strings.LABEL_VIEW_WEEKLY },
    { value: 'month', label: strings.LABEL_VIEW_MONTHLY },
  ];
  return (
    <ToggleButtonGroup
      size="small"
      exclusive
      value={value}
      onChange={(e, v) => v && onChange(v)}
      sx={{
        '& .MuiToggleButton-root': {
          color: MUTED,
          borderColor: BORDER,
          fontSize: 11,
          textTransform: 'none',
          padding: '2px 10px',
          lineHeight: 1.6,
        },
        '& .MuiToggleButton-root.Mui-selected': {
          color: '#e8f0fe',
          backgroundColor: 'rgba(59,149,232,0.15)',
        },
      }}
    >
      {opts.map((o) => <ToggleButton key={o.value} value={o.value}>{o.label}</ToggleButton>)}
    </ToggleButtonGroup>
  );
};

const EmptyChart = () => (
  <div style={{ display: 'flex', width: '100%', height: '100%', alignItems: 'center', justifyContent: 'center' }}>
    <Typography style={{ fontSize: 13, color: MUTED }}>{strings.LABEL_DASHBOARD_NO_DATA}</Typography>
  </div>
);

// Rows follow Monday->Sunday; grid indices are moment().day() (0=Sunday..6=Saturday).
const HEATMAP_DAY_ORDER = [1, 2, 3, 4, 5, 6, 0];
const HEATMAP_HOUR_TICKS = [0, 6, 12, 18];

// Sequential ramp on the brand hue: empty cells stay near the card surface,
// busiest cells reach full brand blue (dark-mode ramp anchors dark->bright).
const heatmapCellColor = (value, max) => {
  if (!value) return 'rgba(255,255,255,0.03)';
  const intensity = max > 0 ? value / max : 0;
  return `rgba(59,149,232,${(0.15 + intensity * 0.85).toFixed(2)})`;
};

// Closed hours: hatched when empty, amber when points were still given
const CLOSED_HATCH = 'repeating-linear-gradient(135deg, rgba(255,255,255,0.07) 0 2px, rgba(255,255,255,0.015) 2px 5px)';
const closedCellColor = (value, max) => {
  if (!value) return CLOSED_HATCH;
  const intensity = max > 0 ? value / max : 0;
  return `rgba(245,181,68,${(0.3 + intensity * 0.7).toFixed(2)})`;
};

// opening: {"1": [{open: 7.5, close: 20}], ..., "7": []}, 1 = Monday,
// decimal hours; a slot closing at or before it opens runs past midnight.
// An hour counts as open when any slot covers part of it
const isHourOpen = (opening, dayIndex, hour) => {
  const isoDay = dayIndex === 0 ? 7 : dayIndex;
  const previousIsoDay = isoDay === 1 ? 7 : isoDay - 1;
  const slots = (day) => (opening[day] || []).filter((s) => typeof s?.open === 'number' && typeof s?.close === 'number');
  const today = slots(isoDay).some((s) => (s.close > s.open
    ? s.open < hour + 1 && s.close > hour
    : s.open < hour + 1));
  return today || slots(previousIsoDay).some((s) => s.close <= s.open && hour < s.close);
};

const LegendSwatch = ({ background, label }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 10, color: MUTED }}>
    <span style={{ width: 12, height: 10, borderRadius: 3, background, display: 'inline-block' }} />
    {label}
  </span>
);

const ActivityHeatmap = ({ grid, opening, onCellClick }) => {
  const dayLabels = [
    strings.LABEL_DAY_MON, strings.LABEL_DAY_TUE, strings.LABEL_DAY_WED,
    strings.LABEL_DAY_THU, strings.LABEL_DAY_FRI, strings.LABEL_DAY_SAT, strings.LABEL_DAY_SUN,
  ];
  const max = grid ? Math.max(0, ...grid.flat()) : 0;

  if (!grid || max === 0) {
    return (
      <div style={{ minHeight: 200, display: 'flex' }}>
        <EmptyChart />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {HEATMAP_DAY_ORDER.map((dayIndex, rowIdx) => (
        <div key={dayIndex} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ width: 28, flexShrink: 0, fontSize: 10, color: MUTED, textAlign: 'right' }}>
            {dayLabels[rowIdx]}
          </div>
          <div style={{ display: 'flex', gap: 2, flex: 1 }}>
            {grid[dayIndex].map((value, hour) => {
              const closed = opening ? !isHourOpen(opening, dayIndex, hour) : false;
              return (
                <MuiTooltip
                  key={hour}
                  title={`${dayLabels[rowIdx]} ${hour}h — ${value} ${strings.LABEL_DASHBOARD_HEATMAP_POINTS_UNIT}${closed ? ` · ${strings.LABEL_DASHBOARD_HEATMAP_CLOSED}` : ''}`}
                  placement="top"
                  arrow
                >
                  <div
                    onClick={value && onCellClick ? () => onCellClick(dayIndex, hour) : undefined}
                    style={{
                      flex: 1,
                      height: 14,
                      borderRadius: 3,
                      background: closed ? closedCellColor(value, max) : heatmapCellColor(value, max),
                      cursor: value && onCellClick ? 'pointer' : 'default',
                    }} />
                </MuiTooltip>
              );
            })}
          </div>
        </div>
      ))}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <div style={{ width: 28, flexShrink: 0 }} />
        <div style={{ display: 'flex', gap: 2, flex: 1 }}>
          {Array.from({ length: 24 }, (_, hour) => (
            <div key={hour} style={{ flex: 1, textAlign: 'center', fontSize: 9, color: MUTED }}>
              {HEATMAP_HOUR_TICKS.includes(hour) ? `${hour}h` : ''}
            </div>
          ))}
        </div>
      </div>
      {opening && (
        <div style={{ display: 'flex', gap: 14, justifyContent: 'flex-end', marginTop: 6 }}>
          <LegendSwatch background="rgba(59,149,232,0.7)" label={strings.LABEL_DASHBOARD_HEATMAP_POINTS} />
          <LegendSwatch background={CLOSED_HATCH} label={strings.LABEL_DASHBOARD_HEATMAP_CLOSED} />
          <LegendSwatch background="rgba(245,181,68,0.8)" label={strings.LABEL_DASHBOARD_HEATMAP_CLOSED_POINTS} />
        </div>
      )}
    </div>
  );
};

/* ── Component ── */

const DashboardUser = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const stats = useSelector((state) => state.stats.stats);
  const selectedCompany = useSelector((state) => state.login.selectedCompany);
  const [pointsData, setPointsData] = useState([]);
  const [pointsDataMonth, setPointsDataMonth] = useState([]);
  const [pointsViewMode, setPointsViewMode] = useState("week");
  const [genderDistribution, setGenderDistribution] = useState([]);
  const [localDistribution, setLocalDistribution] = useState([]);
  const [ageDistribution, setAgeDistribution] = useState([]);
  const [giftsData, setGiftsData] = useState([]);
  const [notificationsData, setNotificationsData] = useState([]);
  const [pointConversionRate, setPointConversionRate] = useState(null);
  const [giftConversionRate, setGiftConversionRate] = useState(null);
  const [retentionRate, setRetentionRate] = useState(null);
  const [ratesData, setRatesData] = useState([]);
  const [ratesDataMonth, setRatesDataMonth] = useState([]);
  const [ratesViewMode, setRatesViewMode] = useState("week");
  const [topClientsViewMode, setTopClientsViewMode] = useState("all");
  const [topClientsMonthOffset, setTopClientsMonthOffset] = useState(0);
  const [heatmapLocal, setHeatmapLocal] = useState(null);
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [activeTab, setActiveTab] = useState(0);
  const [entryDistribution, setEntryDistribution] = useState([]);

  useEffect(() => {
    dispatch(cleanStatsAction());
    dispatch(getStatsUserAction(undefined, undefined, selectedCompany?.id));
  }, [dispatch, selectedCompany]);

  useEffect(() => {
    if (!stats?.genderDistribution) return;

    // Points by week
    const pointsDataTmp = [];
    if (stats.pointsGivenByWeek && stats.lostPointsByWeek) {
      Object.entries(stats.pointsGivenByWeek).forEach(([key, value]) => {
        pointsDataTmp.push({
          week: getWeekLabel(key),
          [strings.LABEL_POINTS_GAINED]: value,
          [strings.LABEL_POINTS_LOST]: stats.lostPointsByWeek[key] || 0,
        });
      });
    }
    setPointsData(pointsDataTmp);

    // Points by month (weekly totals regrouped by the month of each week's Monday)
    const pointsByMonth = {};
    if (stats.pointsGivenByWeek && stats.lostPointsByWeek) {
      Object.entries(stats.pointsGivenByWeek).forEach(([key, value]) => {
        const monthKey = getMonthKey(key);
        if (!pointsByMonth[monthKey]) {
          pointsByMonth[monthKey] = {
            week: getMonthLabel(monthKey),
            [strings.LABEL_POINTS_GAINED]: 0,
            [strings.LABEL_POINTS_LOST]: 0,
          };
        }
        pointsByMonth[monthKey][strings.LABEL_POINTS_GAINED] += value;
        pointsByMonth[monthKey][strings.LABEL_POINTS_LOST] += stats.lostPointsByWeek[key] || 0;
      });
    }
    setPointsDataMonth(Object.keys(pointsByMonth).sort().map((key) => pointsByMonth[key]));

    // Notifications sent (point and gift expiration reminders) by week
    const notificationsDataTmp = [];
    if (stats.pointWarningsByWeek || stats.giftWarningsByWeek) {
      const weeks = [...new Set([
        ...Object.keys(stats.pointWarningsByWeek || {}),
        ...Object.keys(stats.giftWarningsByWeek || {}),
      ])].sort((a, b) => Number(a) - Number(b));
      weeks.forEach((key) => {
        notificationsDataTmp.push({
          week: getWeekLabel(key),
          [strings.LABEL_NOTIFICATIONS_POINT]: stats.pointWarningsByWeek?.[key] || 0,
          [strings.LABEL_NOTIFICATIONS_GIFT]: stats.giftWarningsByWeek?.[key] || 0,
        });
      });
    }
    setNotificationsData(notificationsDataTmp);

    // Reminder conversion rates (% who came back before actually losing the point/gift)
    const computeRate = (conversion) => {
      if (!conversion) return null;
      const converted = Number(conversion.converted) || 0;
      const lost = Number(conversion.lost) || 0;
      const resolved = converted + lost;
      return resolved ? Math.round((converted / resolved) * 100) : null;
    };
    setPointConversionRate(computeRate(stats.pointReminderConversion));
    setGiftConversionRate(computeRate(stats.giftReminderConversion));

    // General point retention rate — all customers, not just those who got a reminder
    const pointEvents = Number(stats.pointEventCountAll) || 0;
    const lostEvents = Number(stats.lostPointCountAll) || 0;
    const totalCycles = pointEvents + lostEvents;
    setRetentionRate(totalCycles ? Math.round((pointEvents / totalCycles) * 100) : null);

    // Indicators over time (retention, point conversion, gift conversion), by week and by month.
    // Percentages are derived from summed raw counts, never averaged directly (a week with
    // 2 resolutions shouldn't weigh the same as a week with 20).
    const rawByWeek = {};
    const ensureWeek = (key) => {
      if (!rawByWeek[key]) {
        rawByWeek[key] = {
          pointEvents: 0, lostEvents: 0,
          pointConverted: 0, pointLost: 0,
          giftConverted: 0, giftLost: 0,
        };
      }
      return rawByWeek[key];
    };
    Object.entries(stats.pointEventCountByWeek || {}).forEach(([key, value]) => {
      ensureWeek(key).pointEvents = value;
    });
    Object.entries(stats.lostPointsByWeek || {}).forEach(([key, value]) => {
      ensureWeek(key).lostEvents = value;
    });
    Object.entries(stats.pointReminderConversionByWeek || {}).forEach(([key, value]) => {
      ensureWeek(key).pointConverted = value.converted;
      ensureWeek(key).pointLost = value.lost;
    });
    Object.entries(stats.giftReminderConversionByWeek || {}).forEach(([key, value]) => {
      ensureWeek(key).giftConverted = value.converted;
      ensureWeek(key).giftLost = value.lost;
    });

    const pct = (num, den) => (den > 0 ? Math.round((num / den) * 100) : undefined);
    const weekKeys = Object.keys(rawByWeek).sort((a, b) => Number(a) - Number(b));

    setRatesData(weekKeys.map((key) => {
      const r = rawByWeek[key];
      return {
        week: getWeekLabel(key),
        [strings.LABEL_DASHBOARD_RETENTION]: pct(r.pointEvents, r.pointEvents + r.lostEvents),
        [strings.LABEL_DASHBOARD_POINT_CONVERSION]: pct(r.pointConverted, r.pointConverted + r.pointLost),
        [strings.LABEL_DASHBOARD_GIFT_CONVERSION]: pct(r.giftConverted, r.giftConverted + r.giftLost),
      };
    }));

    const rawByMonth = {};
    weekKeys.forEach((key) => {
      const monthKey = getMonthKey(key);
      if (!rawByMonth[monthKey]) {
        rawByMonth[monthKey] = {
          pointEvents: 0, lostEvents: 0,
          pointConverted: 0, pointLost: 0,
          giftConverted: 0, giftLost: 0,
        };
      }
      const r = rawByWeek[key];
      const m = rawByMonth[monthKey];
      m.pointEvents += r.pointEvents;
      m.lostEvents += r.lostEvents;
      m.pointConverted += r.pointConverted;
      m.pointLost += r.pointLost;
      m.giftConverted += r.giftConverted;
      m.giftLost += r.giftLost;
    });

    setRatesDataMonth(Object.keys(rawByMonth).sort().map((key) => {
      const m = rawByMonth[key];
      return {
        week: getMonthLabel(key),
        [strings.LABEL_DASHBOARD_RETENTION]: pct(m.pointEvents, m.pointEvents + m.lostEvents),
        [strings.LABEL_DASHBOARD_POINT_CONVERSION]: pct(m.pointConverted, m.pointConverted + m.pointLost),
        [strings.LABEL_DASHBOARD_GIFT_CONVERSION]: pct(m.giftConverted, m.giftConverted + m.giftLost),
      };
    }));

    // Gender
    const totalGender = parseInt(stats.genderDistribution.man) + parseInt(stats.genderDistribution.unknown) + parseInt(stats.genderDistribution.woman);
    if (totalGender) {
      setGenderDistribution([
        { name: strings.LABEL_DASHBOARD_MAN, value: parseInt((stats.genderDistribution.man * 100) / totalGender) },
        { name: strings.LABEL_DASHBOARD_NOT_DEFINED, value: parseInt((stats.genderDistribution.unknown * 100) / totalGender) },
        { name: strings.LABEL_DASHBOARD_WOMAN, value: parseInt(stats.genderDistribution.woman * 100 / totalGender) },
      ]);
    }

    // Age
    let totalAges = 0;
    const localAgeRanges = ageRanges.map((r) => ({ ...r, value: 0 }));
    localAgeRanges.forEach((range, key) => {
      if (range.name === "unknown") {
        localAgeRanges[key].value = stats.ageDistribution.unknown;
        totalAges += stats.ageDistribution.unknown;
      } else {
        Object.entries(stats.ageDistribution).forEach(([age, count]) => {
          if (range.min <= age && age <= range.max) {
            localAgeRanges[key].value += count;
            totalAges += count;
          }
        });
      }
    });
    const resultAges = localAgeRanges.filter((item) => item.value).map((item) => ({
      ...item,
      name: `${item.name} ${parseInt((item.value * 100) / totalAges)}%`,
    }));
    setAgeDistribution(resultAges);

    // Gifts by week by local
    const giftsByWeekByLocal = [];
    if (stats.giftsByWeekByLocal) {
      Object.entries(stats.giftsByWeekByLocal).forEach(([week, locals]) => {
        const weekData = { week: getWeekLabel(week) };
        Object.entries(locals).forEach(([, local]) => { weekData[local.label] = local.count; });
        giftsByWeekByLocal.push(weekData);
      });
    }
    setGiftsData(giftsByWeekByLocal);

    // Points by local
    const pointsByWeekByLocal = [];
    if (stats.pointsByWeekByLocal) {
      Object.entries(stats.pointsByWeekByLocal).forEach(([week, locals]) => {
        const weekData = { week: getWeekLabel(week) };
        Object.entries(locals).forEach(([, points]) => { weekData[points.label] = points.count; });
        pointsByWeekByLocal.push(weekData);
      });
    }
    setLocalDistribution(pointsByWeekByLocal);

    // Entry distribution
    if (stats.entryDistribution) {
      const entryLabels = {
        nfc: strings.LABEL_DASHBOARD_ENTRY_NFC,
        scan: strings.LABEL_DASHBOARD_ENTRY_SCAN,
        code: strings.LABEL_DASHBOARD_ENTRY_CODE,
        unknown: strings.LABEL_DASHBOARD_ENTRY_UNKNOWN,
      };
      setEntryDistribution(
        Object.entries(stats.entryDistribution)
          .filter(([, v]) => v > 0)
          .map(([key, value]) => ({ name: entryLabels[key] || key, value }))
      );
    }
  }, [stats]);

  useEffect(() => {
    const keys = stats?.visitsByLocalDayHour ? Object.keys(stats.visitsByLocalDayHour) : [];
    if (keys.length && !keys.includes(heatmapLocal)) setHeatmapLocal(keys[0]);
  }, [stats, heatmapLocal]);

  const getMonthKeyWithOffset = (offset) => {
    const d = new Date();
    d.setDate(1);
    d.setMonth(d.getMonth() + offset);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  };

  const handleTopClientsPrevMonth = () => {
    const newOffset = topClientsMonthOffset - 1;
    setTopClientsMonthOffset(newOffset);
    dispatch(getStatsUserAction(getMonthKeyWithOffset(newOffset), selectedYear, selectedCompany?.id));
  };

  const handleTopClientsNextMonth = () => {
    if (topClientsMonthOffset >= 0) return;
    const newOffset = topClientsMonthOffset + 1;
    setTopClientsMonthOffset(newOffset);
    dispatch(getStatsUserAction(getMonthKeyWithOffset(newOffset), selectedYear, selectedCompany?.id));
  };

  const handlePrevYear = () => {
    const newYear = selectedYear - 1;
    setSelectedYear(newYear);
    dispatch(getStatsUserAction(getMonthKeyWithOffset(topClientsMonthOffset), newYear, selectedCompany?.id));
  };

  const handleNextYear = () => {
    if (selectedYear >= currentYear) return;
    const newYear = selectedYear + 1;
    setSelectedYear(newYear);
    dispatch(getStatsUserAction(getMonthKeyWithOffset(topClientsMonthOffset), newYear, selectedCompany?.id));
  };

  if (!stats) return null;

  const heatmapLocalOptions = stats.visitsByLocalDayHour
    ? Object.entries(stats.visitsByLocalDayHour).map(([key, value]) => ({ value: key, label: value.label }))
    : [];

  const topClientsMonthLabel = stats.topClientsMonthKey
    ? (() => {
        const [y, m] = stats.topClientsMonthKey.split('-').map(Number);
        return new Date(y, m - 1, 1).toLocaleDateString(strings.getLanguage(), { month: 'long', year: 'numeric' });
      })()
    : '';

  const localKeys = localDistribution.length > 0
    ? [...new Set(localDistribution.flatMap(Object.keys))].filter((k) => k !== 'week')
    : [];
  const giftKeys = giftsData.length > 0
    ? [...new Set(giftsData.flatMap(Object.keys))].filter((k) => k !== 'week')
    : [];
  const localTotals = localKeys
    .map((key) => ({ name: key, value: localDistribution.reduce((sum, w) => sum + (w[key] || 0), 0) }))
    .filter((l) => l.value > 0)
    .sort((a, b) => b.value - a.value);

  return (
    <Box sx={{ flexGrow: 1, mx: 2 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 20 }}>
        <button onClick={handlePrevYear} style={{ background: 'none', border: 'none', cursor: 'pointer', color: MUTED, display: 'flex', padding: 2 }}>
          <ChevronLeftRoundedIcon style={{ fontSize: 16 }} />
        </button>
        <Typography sx={{ color: MUTED, fontSize: 12, letterSpacing: 0.3 }}>
          {stats.statsYear || selectedYear}
        </Typography>
        <button
          onClick={handleNextYear}
          disabled={selectedYear >= currentYear}
          style={{ background: 'none', border: 'none', display: 'flex', padding: 2, cursor: selectedYear >= currentYear ? 'default' : 'pointer', color: selectedYear >= currentYear ? 'rgba(232,240,254,0.15)' : MUTED }}
        >
          <ChevronRightRoundedIcon style={{ fontSize: 16 }} />
        </button>
      </div>

      <DashboardTabs
        active={activeTab}
        onChange={setActiveTab}
        tabs={[
          { key: 'overview', label: strings.LABEL_DASHBOARD_TAB_OVERVIEW },
          { key: 'loyalty', label: strings.LABEL_DASHBOARD_TAB_LOYALTY },
          { key: 'clients', label: strings.LABEL_DASHBOARD_TAB_CLIENTS },
          { key: 'locals', label: strings.LABEL_DASHBOARD_TAB_LOCALS },
        ]}
      />

      {activeTab === 0 && (
        <Grid container spacing={3}>

          {/* Stat cards */}
          <Grid item xs={12} sm={6} md={3}>
            <StatCard icon={PeopleRoundedIcon} label={strings.LABEL_DASHBOARD_ACTIVE_CLIENTS} value={stats.activeClientsLastMonth} info={strings.LABEL_DASHBOARD_ACTIVE_CLIENTS_INFO} accent={BRAND} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard icon={PersonAddRoundedIcon} label={strings.LABEL_DASHBOARD_NEW_CLIENTS} value={stats.newClientsLastMonth} info={strings.LABEL_DASHBOARD_NEW_CLIENTS_INFO} accent="#5bbfb5" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard icon={GroupRoundedIcon} label={strings.LABEL_DASHBOARD_NUMBER_OF_CLIENTS} value={stats.numberOfClients} accent="#8b9fd4" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard icon={StarsRoundedIcon} label={strings.LABEL_DASHBOARD_POINTS_GIVEN} value={stats.pointGivenAll} accent="#6baed6" />
          </Grid>

          {/* Points given/lost by week or month */}
          <Grid item xs={12}>
            <ChartCard
              title={strings.LABEL_DASHBOARD_POINTS_TITLE}
              height={280}
              action={<ChartToggle value={pointsViewMode} onChange={setPointsViewMode} />}
            >
              {(() => {
                const data = pointsViewMode === "month" ? pointsDataMonth : pointsData;
                return data.length ? (
                  <BarChart data={data} margin={{ top: 4, right: 16, left: -16, bottom: 0 }}>
                    <CartesianGrid {...chartGrid} vertical={false} />
                    <XAxis dataKey="week" tick={chartAxis} axisLine={false} tickLine={false} />
                    <YAxis tick={chartAxis} axisLine={false} tickLine={false} allowDecimals={false} />
                    <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
                    <Bar dataKey={strings.LABEL_POINTS_GAINED} fill={BRAND} fillOpacity={0.85} radius={[4, 4, 0, 0]} barSize={14} />
                    <Bar dataKey={strings.LABEL_POINTS_LOST} fill="#5bbfb5" fillOpacity={0.75} radius={[4, 4, 0, 0]} barSize={14} />
                  </BarChart>
                ) : <EmptyChart />;
              })()}
            </ChartCard>
          </Grid>

          {/* Visit heatmap by day/hour, per local */}
          <Grid item xs={12}>
            <div style={card}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 8 }}>
                <Typography style={{ fontSize: 14, fontWeight: 600, color: '#e8f0fe', letterSpacing: 0.3 }}>
                  {strings.LABEL_DASHBOARD_HEATMAP_TITLE}
                </Typography>
                {heatmapLocalOptions.length > 1 && (
                  <ChartToggle value={heatmapLocal} onChange={setHeatmapLocal} options={heatmapLocalOptions} />
                )}
              </div>
              <ActivityHeatmap
                grid={stats.visitsByLocalDayHour?.[heatmapLocal]?.grid}
                opening={stats.visitsByLocalDayHour?.[heatmapLocal]?.opening}
                onCellClick={(dayIndex, hour) => {
                  // The points of that cell: same local, weekday and hour,
                  // over the year shown
                  const year = stats.statsYear || selectedYear;
                  const params = new URLSearchParams({
                    action: 'point',
                    local: heatmapLocal === 'unknown' ? 'none' : heatmapLocal,
                    localLabel: stats.visitsByLocalDayHour?.[heatmapLocal]?.label || '',
                    weekday: dayIndex === 0 ? 7 : dayIndex,
                    hourFrom: hour,
                    hourTo: hour + 1,
                    from: `${year}-01-01`,
                    to: `${year}-12-31`,
                  });
                  navigate(`/activity?${params.toString()}`);
                }}
              />
            </div>
          </Grid>

        </Grid>
      )}

      {activeTab === 1 && (
        <Grid container spacing={3}>

          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              icon={TrendingUpRoundedIcon}
              label={strings.LABEL_DASHBOARD_RETENTION}
              value={retentionRate !== null ? `${retentionRate}%` : "—"}
              info={strings.LABEL_DASHBOARD_RETENTION_INFO}
              accent={BRAND}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              icon={NotificationsActiveRoundedIcon}
              label={strings.LABEL_DASHBOARD_POINT_CONVERSION}
              value={pointConversionRate !== null ? `${pointConversionRate}%` : "—"}
              info={strings.LABEL_DASHBOARD_POINT_CONVERSION_INFO}
              accent="#5bbfb5"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              icon={CardGiftcardRoundedIcon}
              label={strings.LABEL_DASHBOARD_GIFT_CONVERSION}
              value={giftConversionRate !== null ? `${giftConversionRate}%` : "—"}
              info={strings.LABEL_DASHBOARD_GIFT_CONVERSION_INFO}
              accent="#8b9fd4"
            />
          </Grid>

          {/* Expiration reminders sent, by week */}
          <Grid item xs={12} md={6}>
            <ChartCard title={strings.LABEL_DASHBOARD_NOTIFICATIONS_W} height={260}>
              {notificationsData.length ? (
                <BarChart data={notificationsData} margin={{ top: 4, right: 16, left: -16, bottom: 0 }}>
                  <CartesianGrid {...chartGrid} vertical={false} />
                  <XAxis dataKey="week" tick={chartAxis} axisLine={false} tickLine={false} />
                  <YAxis tick={chartAxis} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
                  <Bar dataKey={strings.LABEL_NOTIFICATIONS_POINT} fill={BRAND} fillOpacity={0.85} radius={[4, 4, 0, 0]} barSize={14} />
                  <Bar dataKey={strings.LABEL_NOTIFICATIONS_GIFT} fill="#5bbfb5" fillOpacity={0.75} radius={[4, 4, 0, 0]} barSize={14} />
                </BarChart>
              ) : <EmptyChart />}
            </ChartCard>
          </Grid>

          {/* Retention over time, on its own */}
          <Grid item xs={12} md={6}>
            <ChartCard
              title={strings.LABEL_DASHBOARD_RETENTION_CHART_TITLE}
              height={260}
              action={<ChartToggle value={ratesViewMode} onChange={setRatesViewMode} />}
            >
              {(() => {
                const data = ratesViewMode === "month" ? ratesDataMonth : ratesData;
                return data.length ? (
                  <LineChart data={data} margin={{ top: 4, right: 16, left: -16, bottom: 0 }}>
                    <CartesianGrid {...chartGrid} vertical={false} />
                    <XAxis dataKey="week" tick={chartAxis} axisLine={false} tickLine={false} />
                    <YAxis tick={chartAxis} axisLine={false} tickLine={false} allowDecimals={false} domain={[0, 100]} unit="%" />
                    <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
                    <Line type="monotone" dataKey={strings.LABEL_DASHBOARD_RETENTION} stroke={BRAND} strokeWidth={2} dot={false} connectNulls={false} />
                  </LineChart>
                ) : <EmptyChart />;
              })()}
            </ChartCard>
          </Grid>

          {/* Reminder conversion rates (points + gifts), over time */}
          <Grid item xs={12}>
            <ChartCard
              title={strings.LABEL_DASHBOARD_RETURNS_CHART_TITLE}
              height={260}
              action={<ChartToggle value={ratesViewMode} onChange={setRatesViewMode} />}
            >
              {(() => {
                const data = ratesViewMode === "month" ? ratesDataMonth : ratesData;
                return data.length ? (
                  <LineChart data={data} margin={{ top: 4, right: 16, left: -16, bottom: 0 }}>
                    <CartesianGrid {...chartGrid} vertical={false} />
                    <XAxis dataKey="week" tick={chartAxis} axisLine={false} tickLine={false} />
                    <YAxis tick={chartAxis} axisLine={false} tickLine={false} allowDecimals={false} domain={[0, 100]} unit="%" />
                    <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
                    <Legend iconType="circle" iconSize={8} formatter={(v) => <span style={{ fontSize: 12, color: MUTED }}>{v}</span>} />
                    <Line type="monotone" dataKey={strings.LABEL_DASHBOARD_POINT_CONVERSION} stroke="#5bbfb5" strokeWidth={2} dot={false} connectNulls={false} />
                    <Line type="monotone" dataKey={strings.LABEL_DASHBOARD_GIFT_CONVERSION} stroke="#8b9fd4" strokeWidth={2} dot={false} connectNulls={false} />
                  </LineChart>
                ) : <EmptyChart />;
              })()}
            </ChartCard>
          </Grid>

        </Grid>
      )}

      {activeTab === 2 && (
        <Grid container spacing={3}>

          {/* Top clients by total points earned */}
          <Grid item xs={12} md={6}>
            <TopClientsCard
              clients={topClientsViewMode === 'month' ? stats.topClientsMonth : stats.topClients}
              viewMode={topClientsViewMode}
              onViewModeChange={setTopClientsViewMode}
              monthLabel={topClientsMonthLabel}
              onPrevMonth={handleTopClientsPrevMonth}
              onNextMonth={handleTopClientsNextMonth}
              isCurrentMonth={topClientsMonthOffset >= 0}
            />
          </Grid>

          {/* Entry distribution */}
          <Grid item xs={12} md={6}>
            <ChartCard title={strings.LABEL_DASHBOARD_ENTRY_DISTRIBUTION} height={280}>
              {entryDistribution.length ? (
                <PieChart>
                  <Pie data={entryDistribution} dataKey="value" nameKey="name" cx="50%" cy="45%" outerRadius={90} innerRadius={50} paddingAngle={3}
                    label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {entryDistribution.map((entry, index) => (
                      <Cell key={entry.name} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend iconType="circle" iconSize={8} formatter={(v) => <span style={{ fontSize: 12, color: MUTED }}>{v}</span>} />
                </PieChart>
              ) : <EmptyChart />}
            </ChartCard>
          </Grid>

          {/* Age distribution */}
          <Grid item xs={12} md={6}>
            <ChartCard title={strings.LABEL_DASHBOARD_AGE_DISTRIBUTION} height={300}>
              {ageDistribution.length ? (
                <RadarChart outerRadius={110} data={ageDistribution}>
                  <PolarGrid stroke="rgba(255,255,255,0.08)" />
                  <PolarAngleAxis dataKey="name" tick={{ fill: MUTED, fontSize: 11 }} />
                  <Radar name="Distribution" dataKey="value" fill={BRAND} fillOpacity={0.2} stroke={BRAND} strokeWidth={1.5} />
                  <Tooltip content={<CustomTooltip />} />
                </RadarChart>
              ) : <EmptyChart />}
            </ChartCard>
          </Grid>

          {/* Gender distribution */}
          <Grid item xs={12} md={6}>
            <ChartCard title={strings.LABEL_DASHBOARD_GENDER_DISTRIBUTION} height={300}>
              {genderDistribution.length ? (
                <RadarChart outerRadius={110} data={genderDistribution}>
                  <PolarGrid stroke="rgba(255,255,255,0.08)" />
                  <PolarAngleAxis dataKey="name" tick={{ fill: MUTED, fontSize: 11 }} />
                  <Radar name="Distribution" dataKey="value" fill="#5bbfb5" fillOpacity={0.2} stroke="#5bbfb5" strokeWidth={1.5} />
                  <Tooltip content={<CustomTooltip />} />
                </RadarChart>
              ) : <EmptyChart />}
            </ChartCard>
          </Grid>

        </Grid>
      )}

      {activeTab === 3 && (
        <Grid container spacing={3}>

          {/* Points by local — total share (donut, answers "which local wins") */}
          <Grid item xs={12} md={6}>
            <ChartCard title={strings.LABEL_DASHBOARD_LOCAL_DISTRIBUTION_TOTAL} height={260}>
              {localTotals.length ? (
                <PieChart>
                  <Pie data={localTotals} dataKey="value" nameKey="name" cx="50%" cy="45%" outerRadius={90} innerRadius={50} paddingAngle={3}
                    label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {localTotals.map((entry, index) => (
                      <Cell key={entry.name} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend iconType="circle" iconSize={8} formatter={(v) => <span style={{ fontSize: 12, color: MUTED }}>{v}</span>} />
                </PieChart>
              ) : <EmptyChart />}
            </ChartCard>
          </Grid>

          {/* Gifts by week, stacked by local so the weekly trend stays readable */}
          <Grid item xs={12} md={6}>
            <ChartCard title={strings.LABEL_DASHBOARD_GIFTS_GIVEN_W} height={260}>
              {giftsData.length ? (
                <BarChart data={giftsData} margin={{ top: 4, right: 16, left: -16, bottom: 0 }}>
                  <CartesianGrid {...chartGrid} vertical={false} />
                  <XAxis dataKey="week" tick={chartAxis} axisLine={false} tickLine={false} />
                  <YAxis tick={chartAxis} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
                  {giftKeys.map((key, i) => (
                    <Bar key={key} dataKey={key} stackId="locals" fill={CHART_COLORS[i % CHART_COLORS.length]} fillOpacity={0.85} />
                  ))}
                </BarChart>
              ) : <EmptyChart />}
            </ChartCard>
          </Grid>

          {/* Points by local, by week — stacked so weekly volume and split are both readable */}
          <Grid item xs={12}>
            <ChartCard title={strings.LABEL_DASHBOARD_LOCAL_DISTRIBUTION_W} height={260}>
              {localDistribution.length ? (
                <BarChart data={localDistribution} margin={{ top: 4, right: 16, left: -16, bottom: 0 }}>
                  <CartesianGrid {...chartGrid} vertical={false} />
                  <XAxis dataKey="week" tick={chartAxis} axisLine={false} tickLine={false} />
                  <YAxis tick={chartAxis} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
                  {localKeys.map((key, i) => (
                    <Bar key={key} dataKey={key} stackId="locals" fill={CHART_COLORS[i % CHART_COLORS.length]} fillOpacity={0.85} />
                  ))}
                </BarChart>
              ) : <EmptyChart />}
            </ChartCard>
          </Grid>

        </Grid>
      )}
    </Box>
  );
};

export default DashboardUser;
